import React from 'react'
import { Row, Col, Button, FormGroup, InputGroup } from 'react-bootstrap'
import propTypeSchema from 'react-json-schema-proptypes'
import NumberFormat from 'react-number-format'
import { Amount } from '../types'
import Radium, {Style} from 'radium'
import { assetUrl, markdown, valueOrUndefined } from '../common'

const buttons = {
  Schema: {
    'type': 'object',
    'properties': {
      'color': {'type': 'string'},
      'borderColor': {'type': 'string'},
      'backgroundColor': {'type': 'string'},
      'fontFamily': {'type' : 'string'}
    }
  }
}

const schema = {
  'type': 'object',
  'properties': {
    displayCustomFirst: {'type': 'boolean'},
    displayCurrencyAddon: {'type': 'boolean'},
    displayCustomAmount: {'type': 'boolean'},
    thickAmounts: {'type': 'boolean'},
    centerCustomAmount: {'type': 'boolean'},
    borderButton: {'type': 'boolean'},
    buttonsPerRow: {'type': 'number'},
    buttonsPerRowMobile: {'type': 'number'},
    borderColor: {'type': 'string'},
    lockImage: {'type': 'string'},
    showPleaseMessage: {'type': 'boolean'},
    checkedAmount: {'type': 'number'},
    borderRadius: {'type': 'string'},
    amountsColors: buttons.Schema,
    amountsActiveColors: buttons.Schema,
    customAmountColors: buttons.Schema,
    customAmountActiveColors: buttons.Schema,
    customAmountErrorLabel: {'type': 'string'},
    customAmountFrequency: {'type': 'string'},
    pleaseMessage: {'type': 'string'}
  }
}

@Radium
export default class DonationButtons extends React.Component {
  static propSchema = schema
  static propTypes = propTypeSchema(schema, {
    'required': ['onAmountChanged', 'onValidChanged', 'amounts'],
    'properties': {
      displayErrors: {'type': 'boolean'},
      amount: Amount.Schema,
      amounts: {'type': 'array', 'items': Amount.Schema},
      onAmountChanged: {'isFunction': true},
      onValidChanged: {'isFunction': true}
    }
  })

  static defaultProps = {
    displayCustomAmount: true,
    displayCurrencyAddon: false,
    thickAmounts: false,
    centerCustomAmount: false,
    customAmountTop: true,
    borderButton: true,
    buttonsPerRow: 4,
    buttonsPerRowMobile: 3,
    customAmountFrequency: 'one-time',
    lockImage: 'secure-lock.png',
    showPleaseMessage: true,
    customAmountErrorLabel: 'Required',
    pleaseMessage: 'Please select your gift amount',
    minCustomAmount: 0
  }

  constructor (props) {
    super(props)
    this.state = {
      customAmount: '',
      errorMessage: props.amount ? null : this.props.customAmountErrorLabel,
      isCustom: false
    }
  }

  componentDidUpdate(prevProps){
    if (prevProps.amounts !== this.props.amounts) {
      this.setState({
        isCustom: false,
        customAmount: ''
      }, () => {
        if (typeof this.props.checkedAmount!== 'undefined' && this.props.amounts) {
          this.props.onAmountChanged(this.props.amounts[this.props.checkedAmount])
          this.props.onValidChanged(this.validateAmount(this.props.amounts[this.props.checkedAmount]))
        } else {
          this.props.onAmountChanged(null)
          this.props.onValidChanged(false)
        }
      })
    }
  }

  amountClick = (amount) => {
    this.setState({
      isCustom: false,
      customAmount: ''
    }, () => {
      this.props.onAmountChanged(amount)
      this.props.onValidChanged(this.validateAmount(amount.amount))
    })
  }

  onClickCustomAmount = () => {
    this.setState({
      isCustom: true
    })
  }

  onChangeAmount (modelValue, viewValue) {
    this.setState({
      customAmount: viewValue,
      isCustom: true
    }, () => {
      if (this.validateAmount(modelValue)) {
        this.props.onAmountChanged({
          amount: parseFloat(modelValue),
          type: 'custom',
          frequency: this.props.customAmountFrequency
        })
        this.props.onValidChanged(true)
      } else {
        this.props.onAmountChanged(null)
        this.props.onValidChanged(false)
      }
    })
  }

  validateAmount (value) {
    const valid = (!this.state.isCustom || (value && value > 0 && value >= this.props.minCustomAmount)) || (this.state.isCustom && this.state.customAmount !== '' && this.state.customAmount >= this.props.minCustomAmount)
    this.setState({
      errorMessage: valid ? null : this.props.customAmountErrorLabel
    })
    return valid
  }

  componentDidMount() {
    //This is necessary so parent knows if this form is valid from the get-go
    if (typeof this.props.checkedAmount !== 'undefined' && !this.props.amount && this.props.amounts) {
      this.props.onAmountChanged(this.props.amounts[this.props.checkedAmount])
      this.props.onValidChanged(this.validateAmount(this.props.amounts[this.props.checkedAmount].amount))
    }
    if (this.props.amount) {
      this.props.onValidChanged(this.validateAmount(this.props.amount.amount))
    }
  }

  makeDonationButtonsGeneric (styles) {
    let buttons = []
    let total = this.props.amounts.length
    let cutpoint = total - (total % this.props.buttonsPerRow)
    let cutpointMobile = total - (total % this.props.buttonsPerRowMobile)
    for (let i = 0; i < total; i++) {
      buttons.push(
        <Col className='btnCol'
             xs={12 / (i < cutpointMobile ? this.props.buttonsPerRowMobile : (total % this.props.buttonsPerRowMobile))}
             sm={12 / (i < cutpoint ? this.props.buttonsPerRow : (total % this.props.buttonsPerRow))} key={i}>
          <Button
            className={((this.props.amount && this.props.amount.amount === this.props.amounts[i].amount && this.props.amount.type !== "custom") ? 'active' : '') +
            (this.props.borderButton ? ' corners' : ' noCorner') + (this.props.thickAmounts ? ' thickMargin thickAmount' : '')}
            style={((this.props.amount && this.props.amount.amount === this.props.amounts[i].amount && this.props.amount.type !== "custom") ? styles.amountsActiveColors : styles.amountsColors)}
            bsSize='large'
            onClick={() => this.amountClick(this.props.amounts[i])}>
            {this.props.amounts[i].text || this.props.amounts[i].amount.toString()}
          </Button>
        </Col>
      )
    }
    return <Row className={'amounts' + (this.props.thickAmounts ? ' padding33' : '')}>
      {buttons}
    </Row>
  }

  makeSpecialDonationButtons (styles) {
    let buttons = []
    let total = this.props.specialAmounts?.length
    for (let i = 0; i < total; i++) {
      buttons.push(
        <Row>
          <Col className='btnCol'
               xs={12 }
               sm={12 } key={i}
          >
            <Col md={3} style={{paddingTop: "10px"}}>
            <Button
              className={((this.props.amount && this.props.amount.amount === this.props.specialAmounts[i].amount && this.props.amount.type !== "custom") ? 'active' : '') +
              (this.props.borderButton ? ' corners' : ' noCorner') + (this.props.thickAmounts ? ' thickMargin thickAmount' : '')}
              style={((this.props.amount && this.props.amount.amount === this.props.specialAmounts[i].amount && this.props.amount.type !== "custom") ? styles.amountsActiveColors : styles.amountsColors)}
              bsSize='large'
              onClick={() => this.amountClick(this.props.specialAmounts[i])}>
              {this.props.specialAmounts[i].text || this.props.specialAmounts[i].amount.toString()}
            </Button>
            </Col>
            <Col md={9}>
              <Row className={'tabMessage'} style={{margin: "14px", fontSize: "16px"}}>
                <span className={'text'}>{this.props.specialAmounts[i].message}</span>
              </Row>
            </Col>
          </Col>
        </Row>
      )
    }
    return <Row className={'amounts' + (this.props.thickAmounts ? ' padding33' : '')}>
      {buttons}
    </Row>
  }

  renderCustomAmount (styles) {
    return <FormGroup className={this.props.displayCustomFirst ? 'top' : ''}>
      <InputGroup className={'fullWidth'}>
        {this.props.displayCurrencyAddon &&
        <InputGroup.Addon className={'customAmountAddon'}
          style={this.state.isCustom ? styles.inputActiveAddon :
            styles.inputAddon}>$</InputGroup.Addon>
        }
        <Style
          scopeSelector=".amounts"
          rules={{
            'input::placeholder': {
              color: this.state.isCustom ? styles.customAmountActiveColors.color : styles.customAmountColors.color
            }
          }}
        />
        <NumberFormat
          value={this.state.customAmount}
          isAllowed={(values) => {
            const {value} = values
            let format = /^\d{0,6}(\.\d{0,2})?$/
            return value.toString().match(format)
          }}
          className={'form-control amount' + (!this.props.centerCustomAmount ? ' controlLeft' : '') +
          (this.state.isCustom ? ' active' : '') + (this.props.borderButton ? ' corners' : ' noCorner') +
          (this.props.displayCurrencyAddon ? ' customAmountThick' : ' ')}
          style={this.state.isCustom ? styles.customAmountActiveColors : styles.customAmountColors}
          placeholder={this.props.displayCurrencyAddon ? 'Other' : '$ Other'}
          isNumericString
          prefix={this.props.displayCurrencyAddon ? '' : '$ '}
          onValueChange={(values) => {
            const {value} = values
            this.onChangeAmount(value, value)
          }}
          onClick={this.onClickCustomAmount.bind(this)}
          thousandSeparator={','} />
      </InputGroup>
    </FormGroup>
  }

  renderDonationButtons () {
    const styles = {
      secureLock: {
        backgroundImage: `url(${assetUrl(this.props.lockImage)})`
      },
      amountsColors: valueOrUndefined(this.props, 'amountsColors'),
      amountsActiveColors: valueOrUndefined(this.props, 'amountsActiveColors'),
      customAmountColors: valueOrUndefined(this.props, 'customAmountColors'),
      customAmountActiveColors: valueOrUndefined(this.props, 'customAmountActiveColors'),
      inputAddon: {
        color: valueOrUndefined(this.props, 'customAmountColors.color'),
        backgroundColor: valueOrUndefined(this.props, 'customAmountColors.borderColor'),
        fontFamily: valueOrUndefined(this.props, 'customAmountColors.fontFamily')
      },
      inputActiveAddon: {
        color: valueOrUndefined(this.props, 'customAmountActiveColors.color'),
        backgroundColor: valueOrUndefined(this.props, 'customAmountActiveColors.borderColor'),
        fontFamily: valueOrUndefined(this.props, 'customAmountActiveColors.fontFamily')
      }
    }
    styles['amountsColors']['borderRadius'] =  valueOrUndefined(this.props, 'borderRadius')
    styles['amountsActiveColors']['borderRadius'] =  valueOrUndefined(this.props, 'borderRadius')
    return <div>
      {this.props.showPleaseMessage &&
      <Row className='pleaseDonate'>
        <Col xs={12}>
          <span>{this.props.pleaseMessage}</span>
          <div className='secureLock' style={[styles.secureLock]} />
        </Col>
      </Row>
      }
      <div>
        {this.props.displayCustomFirst && this.props.displayCustomAmount &&
        <Row className={'amounts large-margin' + (this.props.thickAmounts ? ' padding33' : '')}>
          <Col xs={12} className={this.state.errorMessage && this.props.displayErrors ? ' has-error' : ''}>
            {this.props.displayErrors && this.state.errorMessage && this.state.isCustom &&
            <label>{this.state.errorMessage}</label>}
            {this.renderCustomAmount(styles)}
          </Col>
        </Row>
        }
        {this.makeDonationButtonsGeneric(styles)}
        {this.makeSpecialDonationButtons(styles)}
        {!this.props.displayCustomFirst && this.props.displayCustomAmount &&
        <Row className={'amounts large-margin' + (this.props.thickAmounts ? ' padding33' : '')}>
          <Col xs={12} className={this.state.errorMessage && this.props.displayErrors ? ' has-error' : ''}>
            {this.props.displayErrors && this.state.errorMessage && this.state.isCustom &&
            <label>{this.state.errorMessage}</label>}
            {this.renderCustomAmount(styles)}
          </Col>
        </Row>
        }
      </div>
    </div>
  }

  render () {
    return <div className='donationButtons'>
      {this.renderDonationButtons()}
    </div>
  }
}
